import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  ExpandableText,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { CompactConnectionContactPreviewChip } from "@/components/ui/ContactPreviewChip/CompactConnectionContactPreviewChip";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { useVehiclesViewModel } from "@/hooks/matter-view-models/use-vehicles-view-model";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  Matter,
  MatterContactConnection,
  MatterVehicle,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  getVehicleContactConnection,
  getVehicleContactConnections,
} from "@/lib/utils/connections/vehicle-contact-connections";
import { formatInsuranceTitle, formatVehicleTitle } from "@/lib/utils/format";
import { VehicleIcon } from "@/lib/utils/iconUtils";
import {
  getMatterContactConnections,
  getlabel,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { getUnassignedMatterVehicles } from "@/lib/utils/matterPartyCoverage";
import { ErrorBoundary } from "@sentry/react";
import { upperCase } from "lodash";
import { Pencil } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useNumberFormatter } from "react-aria";

interface MatterVehiclesProps {
  matter: Matter;
  searchVehicleId?: string;
  showHighlights?: boolean;
  contactConnectionLabel?: "Client" | "Defendant";
  showCreateButton?: boolean;
  showTitle?: boolean;
  excludeContactConnectionLabels?: MatterContactConnection["label"][];
  includeUnassigned?: boolean;
}

function MatterVehiclesDangerous({
  matter,
  searchVehicleId,
  showHighlights = true,
  contactConnectionLabel,
  showCreateButton = true,
  showTitle = false,
  excludeContactConnectionLabels,
  includeUnassigned = false,
}: MatterVehiclesProps) {
  const formatter = useNumberFormatter({
    style: "decimal",
    minimumFractionDigits: 0,
  });
  const { hasActionPermission } = useUser();
  const hasViewPermission = Boolean(true);
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterVehicle,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterVehicle,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterVehicle,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canOpenExistingVehicle = hasUpdatePermission || hasDeletePermission;

  // Global slideover management
  const { openVehicleModal } = useGlobalModal();
  const { handleVehicleSearch } = useHandleSearchParams();
  const processedSearches = useRef<Set<string>>(new Set());
  const { clientVehicles, defendantVehicles } = useVehiclesViewModel(matter.id);
  const matterContactConnections = useMemo(
    () => getMatterContactConnections(matter),
    [matter],
  );

  const unassignedVehicles = useMemo(
    () => getUnassignedMatterVehicles(matter),
    [matter],
  );
  const unassignedVehicleIds = useMemo(
    () => new Set(unassignedVehicles.map((vehicle) => vehicle.id)),
    [unassignedVehicles],
  );
  const vehicles = useMemo(() => {
    const filteredVehicles =
      contactConnectionLabel === "Client"
        ? clientVehicles
        : contactConnectionLabel === "Defendant"
          ? defendantVehicles
          : matter.vehicles;
    const baseVehicles = includeUnassigned
      ? [...filteredVehicles, ...unassignedVehicles]
      : filteredVehicles;

    if (!excludeContactConnectionLabels?.length) {
      return baseVehicles;
    }

    const excludedContactIds = new Set(
      matterContactConnections
        .filter((connection) =>
          excludeContactConnectionLabels.includes(getlabel(connection)),
        )
        .map((connection) => connection.contactId),
    );

    if (excludedContactIds.size === 0) {
      return baseVehicles;
    }

    return baseVehicles.filter((vehicle) => {
      return !vehicle.connections.some((connection) =>
        excludedContactIds.has(connection.targetObjectId),
      );
    });
  }, [
    contactConnectionLabel,
    clientVehicles,
    defendantVehicles,
    matter.vehicles,
    matterContactConnections,
    excludeContactConnectionLabels,
    includeUnassigned,
    unassignedVehicles,
  ]);

  const defendants = matterContactConnections.filter(
    (connection) => connection.label === "Defendant",
  );

  // Handle URL-based vehicle search using the global hook
  useEffect(() => {
    if (!searchVehicleId) return;

    const searchKey = `vehicle-${searchVehicleId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handleVehicleSearch(matter, searchVehicleId);
  }, [searchVehicleId, matter, handleVehicleSearch]);

  const pageHighlightCards = useMemo(() => {
    return [
      {
        title: "Vehicles",
        value: vehicles.length,
      },
      {
        title: "Total repair estimate",
        value: vehicles.reduce(
          (acc, vehicle) => acc + (vehicle.repairEstimate ?? 0),
          0,
        ),
        isCurrency: true,
      },
      {
        title: "Total loss",
        value: vehicles.reduce(
          (acc, vehicle) => acc + (vehicle.totalLoss ?? 0),
          0,
        ),
        isCurrency: true,
      },
    ];
  }, [vehicles]);

  // Handler for creating a new vehicle
  const handleCreateVehicle = () => {
    openVehicleModal({
      matterId: matter.id,
      editing: false,
      initialTab: "details",
      enforceRequiredContact: contactConnectionLabel,
    });
  };

  // Handler for editing an existing vehicle
  const handleEditVehicle = (vehicle: MatterVehicle) => {
    openVehicleModal({
      matterId: matter.id,
      vehicleId: vehicle.id,
      editing: true,
      initialTab: "details",
      enforceRequiredContact: contactConnectionLabel,
    });
  };

  return (
    <div
      className={
        showTitle ? "flex flex-col gap-y-4" : "flex flex-col gap-y-4 p-6"
      }
    >
      {showTitle ? (
        <div className="flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-2">
          <Text variant="title">
            {hasViewPermission ? `Vehicles (${vehicles.length})` : "Vehicles"}
          </Text>
          {showCreateButton && hasCreatePermission && (
            <TooltipWrapper
              message={
                contactConnectionLabel === "Defendant" &&
                defendants.length === 0
                  ? "You must add a defendant to create a vehicle."
                  : ""
              }
            >
              <div>
                <Button
                  onClick={handleCreateVehicle}
                  disabled={
                    contactConnectionLabel === "Defendant" &&
                    defendants.length === 0
                  }
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <VehicleIcon className="h-4 w-4" />
                  Create vehicle
                </Button>
              </div>
            </TooltipWrapper>
          )}
        </div>
      ) : (
        <div className="flex w-full flex-1 grow flex-wrap items-center gap-x-2">
          <div className="flex grow" />
          {showCreateButton && hasCreatePermission && (
            <TooltipWrapper
              message={
                contactConnectionLabel === "Defendant" &&
                defendants.length === 0
                  ? "You must add a defendant to create a vehicle."
                  : ""
              }
            >
              <div>
                <Button
                  onClick={handleCreateVehicle}
                  disabled={
                    contactConnectionLabel === "Defendant" &&
                    defendants.length === 0
                  }
                  variant="outline"
                  className="bg-background"
                >
                  <VehicleIcon className="w-4" />
                  Create vehicle
                </Button>
              </div>
            </TooltipWrapper>
          )}
        </div>
      )}
      {hasViewPermission && showHighlights && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {pageHighlightCards.map((item) => (
            <div
              key={item.title}
              className="border-border bg-accent/50 rounded-lg border-none p-6"
            >
              <Text
                variant="caption"
                color="primary"
                className="mb-2 font-medium tracking-wider uppercase"
              >
                {upperCase(item.title)}
              </Text>
              <Text
                variant="title"
                color="primary"
                className="text-3xl leading-9"
              >
                {item.isCurrency ? (
                  <CurrencyWrapper value={item.value} />
                ) : (
                  item.value
                )}
              </Text>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-y-4">
        {!hasViewPermission && (
          <Card className="w-full border-2">
            <CardHeader className="space-y-1">
              <Text variant="subHeading">
                You do not have permission to view vehicles.
              </Text>
            </CardHeader>
          </Card>
        )}
        {hasViewPermission && vehicles.length === 0 && (
          <Card className="w-full border-2 border-dashed">
            <CardHeader className="space-y-1">
              <Text
                variant="body"
                className="text-muted-foreground text-center font-semibold"
              >
                You haven&apos;t added a vehicle yet.
              </Text>
              <Text variant="subHeading" className="text-center">
                {showCreateButton
                  ? "Create your first by clicking 'Create vehicle'"
                  : "No vehicles have been added yet."}
              </Text>
            </CardHeader>
          </Card>
        )}
        {hasViewPermission &&
          vehicles.map((vehicle) => {
            const vehicleId = vehicle.id;
            const registeredOwner = getVehicleContactConnection(
              vehicle,
              "Registered Owner",
            );
            const driver = getVehicleContactConnection(vehicle, "Driver");
            const autoShops = getVehicleContactConnections(
              vehicle,
              "Auto Shop",
            );
            const passengers = getVehicleContactConnections(
              vehicle,
              "Passenger",
            );
            const pedestrians = getVehicleContactConnections(
              vehicle,
              "Pedestrian",
            );
            const insurance = formatInsuranceTitle({
              insurance: matter.insurances.find(
                (insurance) => insurance.id === vehicle.insuranceId,
              ),
            });
            return (
              <Card
                key={vehicleId}
                className="border-border group w-full"
                data-testid="vehicle-card"
              >
                <div className="flex items-start justify-between p-6 pb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Text variant="cardTitle" className="mb-0 break-all">
                        {formatVehicleTitle(vehicle)}
                      </Text>
                      {unassignedVehicleIds.has(vehicleId) ? (
                        <Badge variant="secondary">Unassigned</Badge>
                      ) : null}
                    </div>
                    <div className="mt-2 flex flex-col gap-y-1">
                      <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                        <Text variant="subHeading" className="self-start">
                          Registered owner:
                        </Text>
                        {registeredOwner ? (
                          <CompactConnectionContactPreviewChip
                            connection={registeredOwner}
                            matterId={matter.id}
                          />
                        ) : (
                          <Text variant="body" className="font-semibold">
                            -
                          </Text>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                        <Text variant="subHeading" className="self-start">
                          Driver:
                        </Text>
                        {driver ? (
                          <CompactConnectionContactPreviewChip
                            connection={driver}
                            matterId={matter.id}
                          />
                        ) : (
                          <Text variant="body" className="font-semibold">
                            -
                          </Text>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                        <Text variant="subHeading" className="self-start">
                          Auto shops:
                        </Text>
                        {autoShops.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {autoShops.map((autoShop) => (
                              <CompactConnectionContactPreviewChip
                                key={autoShop.id}
                                connection={autoShop}
                                matterId={matter.id}
                              />
                            ))}
                          </div>
                        ) : (
                          <Text variant="body" className="font-semibold">
                            -
                          </Text>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                        <Text variant="subHeading" className="self-start">
                          Passengers:
                        </Text>
                        {passengers.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {passengers.map((passenger) => (
                              <CompactConnectionContactPreviewChip
                                key={passenger.id}
                                connection={passenger}
                                matterId={matter.id}
                              />
                            ))}
                          </div>
                        ) : (
                          <Text variant="body" className="font-semibold">
                            -
                          </Text>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                        <Text variant="subHeading" className="self-start">
                          Pedestrians:
                        </Text>
                        {pedestrians.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {pedestrians.map((pedestrian) => (
                              <CompactConnectionContactPreviewChip
                                key={pedestrian.id}
                                connection={pedestrian}
                                matterId={matter.id}
                              />
                            ))}
                          </div>
                        ) : (
                          <Text variant="body" className="font-semibold">
                            -
                          </Text>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                        <Text variant="subHeading" className="self-start">
                          Insurance:
                        </Text>
                        {insurance?.title ? (
                          <Badge variant="outline">{insurance.title}</Badge>
                        ) : (
                          <Text variant="body" className="font-semibold">
                            -
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                  {canOpenExistingVehicle && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      data-testid={
                        contactConnectionLabel === "Client"
                          ? "edit-client-vehicle-button"
                          : contactConnectionLabel === "Defendant"
                            ? "edit-defendant-vehicle-button"
                            : "edit-other-vehicle-button"
                      }
                      onClick={() => {
                        handleEditVehicle(vehicle);
                      }}
                      className={cn(
                        "text-muted-foreground hover:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 h-8 w-8 shrink-0",
                        "opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100",
                      )}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <CardContent className="px-6 pt-0 pb-4">
                  <div
                    className="max-h-[min(50vh,22rem)] overflow-y-auto overscroll-y-contain pr-1"
                    role="region"
                    aria-label="Vehicle details"
                  >
                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 text-sm md:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Year
                        </Text>
                        <Text variant="subHeading">{vehicle.year || "-"}</Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Color
                        </Text>
                        <Text variant="subHeading">{vehicle.color || "-"}</Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          VIN
                        </Text>
                        <Text variant="subHeading">{vehicle.vin || "-"}</Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          License plate
                        </Text>
                        <Text variant="subHeading">
                          {vehicle.licensePlateNumber || "-"}
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Registration state
                        </Text>
                        <Text variant="subHeading">
                          {vehicle.registrationState || "-"}
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Mileage
                        </Text>
                        <Text variant="subHeading">
                          {vehicle.mileage
                            ? formatter.format(vehicle.mileage)
                            : "-"}
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Damage severity
                        </Text>
                        <Text variant="subHeading">
                          {vehicle.damageSeverity || "-"}
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Repair estimate
                        </Text>
                        <Text variant="subHeading">
                          <CurrencyWrapper
                            value={vehicle.repairEstimate ?? undefined}
                          />
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Total loss
                        </Text>
                        <Text variant="subHeading">
                          <CurrencyWrapper
                            value={vehicle.totalLoss ?? undefined}
                          />
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Description
                        </Text>
                        {vehicle.description ? (
                          <ExpandableText
                            text={vehicle.description}
                            maxLines={3}
                            className="whitespace-pre-wrap"
                          />
                        ) : (
                          <Text
                            variant="subHeading"
                            className="whitespace-pre-wrap"
                          >
                            -
                          </Text>
                        )}
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Deductible paid amount
                        </Text>
                        <Text variant="subHeading">
                          <CurrencyWrapper
                            value={vehicle.deductiblePaidAmount ?? undefined}
                          />
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Deductible reimbursed
                        </Text>
                        <Text variant="subHeading">
                          {vehicle.deductibleReimbursed ? "Yes" : "No"}
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Loss of use
                        </Text>
                        <Text variant="subHeading">
                          {vehicle.lossOfUse ? "Yes" : "No"}
                        </Text>
                      </div>
                      {vehicle.lossOfUse && (
                        <div>
                          <Text variant="subHeading" className="font-medium">
                            Loss of use amount
                          </Text>
                          <Text variant="subHeading">
                            <CurrencyWrapper
                              value={vehicle.lossOfUseAmount ?? undefined}
                            />
                          </Text>
                        </div>
                      )}
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Loss of use reimbursed
                        </Text>
                        <Text variant="subHeading">
                          {vehicle.lossOfUseReimbursed ? "Yes" : "No"}
                        </Text>
                      </div>
                      <div>
                        <Text variant="subHeading" className="font-medium">
                          Loss of use details
                        </Text>
                        {vehicle.lossOfUseDetails ? (
                          <ExpandableText
                            text={vehicle.lossOfUseDetails}
                            maxLines={3}
                            className="whitespace-pre-wrap"
                          />
                        ) : (
                          <Text
                            variant="subHeading"
                            className="whitespace-pre-wrap"
                          >
                            -
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>
    </div>
  );
}

export default function MatterVehicles(props: MatterVehiclesProps) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <MatterVehiclesDangerous {...props} />
    </ErrorBoundary>
  );
}
